import React, { useState, useEffect, useCallback } from 'react';
import { clinicService } from '../../../services/api';

function normalize(s) {
  return (s || '').trim().toLowerCase().replace(/\s+/g, ' ');
}

function findMatches(doctorName, clinicDoctors) {
  const n = normalize(doctorName);
  if (!n) return [];
  return clinicDoctors.filter((d) => {
    const pn = normalize(d.primary_name);
    return pn === n || pn.includes(n) || n.includes(pn);
  });
}

const Step2_Map = ({ clinicId, items, onBack, onNext, error, setError }) => {
  const [clinicDoctors, setClinicDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mappings, setMappings] = useState([]);
  const [unmapped, setUnmapped] = useState([]);

  useEffect(() => {
    const load = async () => {
      if (!clinicId) return;
      setLoading(true);
      try {
        const data = await clinicService.getClinicDoctors(clinicId);
        setClinicDoctors(data.doctors || []);
      } catch (err) {
        setError(err.response?.data?.detail || err.message || 'Failed to load doctors');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [clinicId, setError]);

  useEffect(() => {
    if (!items?.length || !clinicDoctors.length) return;
    const mapped = [];
    const unmappedList = [];
    for (const item of items) {
      const matches = findMatches(item.doctor_name, clinicDoctors);
      if (matches.length === 1) {
        mapped.push({
          doctor_name: item.doctor_name,
          service_provided: item.service_provided,
          doctor: matches[0],
        });
      } else if (matches.length > 1) {
        unmappedList.push({
          ...item,
          matches,
          selectedDoctorId: null,
        });
      } else {
        unmappedList.push({
          ...item,
          matches: [],
          selectedDoctorId: null,
        });
      }
    }
    setMappings(mapped);
    setUnmapped(unmappedList);
  }, [items, clinicDoctors]);

  const handleSelectDoctor = useCallback((idx, doctorId) => {
    setUnmapped((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], selectedDoctorId: doctorId };
      return next;
    });
  }, []);

  const handleIgnore = useCallback((idx) => {
    setUnmapped((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  const handleNext = useCallback(() => {
    const toAdd = unmapped
      .filter((u) => u.selectedDoctorId != null)
      .map((u) => {
        const doctor = u.matches?.find((d) => d.id === u.selectedDoctorId) || clinicDoctors.find((d) => d.id === u.selectedDoctorId);
        return {
          doctor_name: u.doctor_name,
          service_provided: u.service_provided,
          doctor,
        };
      });
    const allMappings = [...mappings, ...toAdd];
    onNext({ clinicId, mappings: allMappings });
  }, [clinicId, mappings, unmapped, clinicDoctors, onNext]);

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin h-8 w-8 border-2 border-blue-600 border-t-transparent rounded-full mx-auto mb-2" />
        <p className="text-gray-600">Loading clinic doctors...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 2: Map Doctors</h2>
        <p className="text-gray-600">
          {mappings.length} auto-matched. {unmapped.length} need manual match or ignore.
        </p>
      </div>

      {mappings.length > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <h3 className="font-medium text-green-800 mb-2">Matched ({mappings.length})</h3>
          <ul className="text-sm text-green-700 space-y-1">
            {mappings.map((m, i) => (
              <li key={i}>
                {m.doctor_name} → {m.doctor.primary_name}
              </li>
            ))}
          </ul>
        </div>
      )}

      {unmapped.length > 0 && (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-4">
          <h3 className="font-medium text-gray-800">Unmatched – select doctor or ignore</h3>
          {unmapped.map((u, idx) => (
            <div key={idx} className="flex flex-wrap items-center gap-3 p-3 bg-white border rounded">
              <span className="font-medium text-gray-700">{u.doctor_name}</span>
              <span className="text-gray-500">→</span>
              {u.matches?.length > 0 ? (
                <>
                  <select
                    value={u.selectedDoctorId || ''}
                    onChange={(e) => handleSelectDoctor(idx, e.target.value ? parseInt(e.target.value, 10) : null)}
                    className="px-2 py-1 border border-gray-300 rounded text-sm"
                  >
                    <option value="">-- Select --</option>
                    {u.matches.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.primary_name}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => handleIgnore(idx)}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Ignore
                  </button>
                </>
              ) : (
                <>
                  <span className="text-gray-500 text-sm">No match</span>
                  <button
                    type="button"
                    onClick={() => handleIgnore(idx)}
                    className="text-red-600 hover:text-red-700 text-sm"
                  >
                    Ignore
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">{error}</div>
      )}

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 font-medium"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={mappings.length + unmapped.filter((u) => u.selectedDoctorId != null).length === 0}
          className="px-8 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          Next ({mappings.length + unmapped.filter((u) => u.selectedDoctorId != null).length} to update)
        </button>
      </div>
    </div>
  );
};

export default Step2_Map;
